import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";

/**
 * Glassmorphic confirmation dialog for destructive reset actions.
 * Renders into document.body via portal so parent transforms can't trap it.
 *
 * Props:
 *   open       — controls visibility
 *   title      — bold headline (e.g. "Reset Tax Packet?")
 *   body       — descriptive paragraph
 *   confirmLabel — text on the danger button
 *   busy       — disable buttons + show spinner
 *   onCancel   — clicked cancel or backdrop
 *   onConfirm  — clicked the danger button
 *   testid     — data-testid prefix
 */
export function ConfirmResetDialog({
  open,
  title = "Are you sure?",
  body = "⚠️ Are you sure you want to permanently clear this data ledger? This action cannot be undone.",
  confirmLabel = "Yes, wipe it all",
  busy = false,
  onCancel,
  onConfirm,
  testid = "confirm-reset",
}) {
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      data-testid={`${testid}-overlay`}
      className="fixed inset-0 z-[9900] flex items-center justify-center px-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
      style={{
        background: "rgba(2,6,23,0.72)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        animation: "confirm-reset-fade 200ms ease-out",
      }}
    >
      <div
        data-testid={`${testid}-panel`}
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(30,15,60,0.92), rgba(15,23,42,0.94))",
          border: "1px solid rgba(244,63,94,0.55)",
          boxShadow: "0 30px 70px -20px rgba(244,63,94,0.55), 0 0 0 1px rgba(244,63,94,0.15) inset",
          animation: "confirm-reset-pop 240ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <button
          type="button"
          onClick={() => !busy && onCancel?.()}
          disabled={busy}
          data-testid={`${testid}-close`}
          className="absolute top-3 right-3 text-slate-400 hover:text-white p-1 rounded"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
              style={{
                background: "rgba(244,63,94,0.18)",
                border: "1px solid rgba(244,63,94,0.55)",
                boxShadow: "0 0 22px rgba(244,63,94,0.45)",
              }}
            >
              <AlertTriangle size={18} className="text-rose-300" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-rose-300">
                Destructive Action
              </div>
              <div className="text-white text-lg font-bold tracking-tight">
                {title}
              </div>
            </div>
          </div>
          <p className="text-slate-300 text-sm leading-relaxed">{body}</p>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-1">
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            disabled={busy}
            data-testid={`${testid}-cancel`}
            className="px-4 h-10 rounded-lg text-slate-300 text-xs font-mono uppercase tracking-widest hover:text-white hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => !busy && onConfirm?.()}
            disabled={busy}
            data-testid={`${testid}-confirm`}
            className={`px-4 h-10 rounded-lg text-white text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-transform ${busy ? "opacity-70 cursor-wait" : "hover:-translate-y-px"}`}
            style={{
              background: "linear-gradient(135deg, #f43f5e, #b91c1c)",
              boxShadow: "0 12px 26px -10px rgba(244,63,94,0.7)",
              letterSpacing: "0.14em",
            }}
          >
            {busy && (
              <span
                className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent"
                style={{ animation: "confirm-reset-spin 0.9s linear infinite" }}
              />
            )}
            {busy ? "Wiping…" : confirmLabel}
          </button>
        </div>
        <style>{`
          @keyframes confirm-reset-fade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes confirm-reset-pop {
            from { transform: translateY(6px) scale(0.97); opacity: 0; }
            to   { transform: translateY(0)   scale(1);    opacity: 1; }
          }
          @keyframes confirm-reset-spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    </div>,
    document.body,
  );
}

export default ConfirmResetDialog;
